import React, { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Image from '~/components/Common/Image';
import ImageAdjustmentControls from '~/components/Common/Images/ImageAdjustmentControls';
import type { ImageAdjustments } from '~/components/Common/Images/ImageAdjustmentControls';

import type { MediaImage } from '../types';

import {
  buildDefaultEdit,
  buildImageFilter,
  calculateCrop,
  getFixedCrop,
  getImageWarmthOverlaySx,
  mergeEditWithDefaults,
} from './ImageEditorUtils';
import type { CropArea, CropHandle, LocalImageEdit } from './ImageEditorUtils';

export type { LocalImageEdit } from './ImageEditorUtils';

const FIXED_CROP_ASPECT_RATIO = 16 / 9;

interface Props {
  edit?: LocalImageEdit;
  image?: MediaImage;
  open: boolean;
  onClose: () => void;
  onDeleteImage: (image: MediaImage) => void;
  onSave: (publicId: string, edit: LocalImageEdit) => Promise<void> | void;
}

interface CropDragState {
  crop: CropArea;
  cropMode: LocalImageEdit['cropMode'];
  handle: CropHandle;
  height: number;
  startX: number;
  startY: number;
  width: number;
}

export default function ImageEditor({ edit, image, open, onClose, onDeleteImage, onSave }: Props) {
  const defaultEdit = useMemo(() => (image ? buildDefaultEdit(image) : undefined), [image]);
  const [draftEdit, setDraftEdit] = useState<LocalImageEdit | undefined>(() =>
    mergeEditWithDefaults(defaultEdit, edit),
  );
  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  const cropDragStateRef = useRef<CropDragState | null>(null);
  const cropDragCleanupRef = useRef<(() => void) | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState(FIXED_CROP_ASPECT_RATIO);
  const cropMaskSx = useMemo(() => (draftEdit ? getCropMaskSx(draftEdit.crop) : []), [draftEdit]);

  useEffect(() => {
    setDraftEdit(mergeEditWithDefaults(defaultEdit, edit));
  }, [defaultEdit, edit]);

  useEffect(() => {
    return () => {
      cropDragCleanupRef.current?.();
      cropDragCleanupRef.current = null;
      cropDragStateRef.current = null;
    };
  }, []);

  if (!image || !draftEdit) {
    return null;
  }

  const warmthOverlay = getImageWarmthOverlaySx(draftEdit);
  const visibleCropHandles = draftEdit.cropMode === 'fixed' ? fixedCropHandles : cropHandles;
  const adjustments: ImageAdjustments = {
    brightness: draftEdit.brightness,
    contrast: draftEdit.contrast,
    warmth: draftEdit.warmth,
  };

  const handleAdjustmentsChange = (nextAdjustments: ImageAdjustments) => {
    setDraftEdit((currentEdit) => (currentEdit ? { ...currentEdit, ...nextAdjustments } : currentEdit));
  };

  const handleCropModeChange = (nextCropMode: LocalImageEdit['cropMode']) => {
    setDraftEdit((currentEdit) =>
      currentEdit
        ? {
            ...currentEdit,
            crop:
              nextCropMode === 'fixed'
                ? getFixedCrop(currentEdit.crop, imageAspectRatio, FIXED_CROP_ASPECT_RATIO)
                : currentEdit.crop,
            cropMode: nextCropMode,
          }
        : currentEdit,
    );
  };

  const handleResetAdjustments = () => {
    setDraftEdit((currentEdit) =>
      currentEdit && defaultEdit
        ? {
            ...currentEdit,
            brightness: 0,
            contrast: 0,
            crop: defaultEdit.crop,
            cropMode: defaultEdit.cropMode,
            warmth: 0,
          }
        : currentEdit,
    );
  };

  const updateCrop = (dragState: CropDragState, event: globalThis.PointerEvent) => {
    const deltaX = ((event.clientX - dragState.startX) / dragState.width) * 100;
    const deltaY = ((event.clientY - dragState.startY) / dragState.height) * 100;
    const nextCrop = calculateCrop(
      dragState.handle,
      dragState.crop,
      deltaX,
      deltaY,
      dragState.cropMode,
      imageAspectRatio,
      FIXED_CROP_ASPECT_RATIO,
    );

    setDraftEdit((currentEdit) => (currentEdit ? { ...currentEdit, crop: nextCrop } : currentEdit));
  };

  const removeCropDragListeners = () => {
    cropDragCleanupRef.current?.();
    cropDragCleanupRef.current = null;
  };

  const handleStartCropDrag = (handle: CropHandle) => (event: PointerEvent<HTMLElement>) => {
    const container = cropContainerRef.current;

    if (!container) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const containerRect = container.getBoundingClientRect();
    cropDragStateRef.current = {
      crop: draftEdit.crop,
      cropMode: draftEdit.cropMode,
      handle,
      height: containerRect.height,
      startX: event.clientX,
      startY: event.clientY,
      width: containerRect.width,
    };

    const handleCropPointerMove = (pointerEvent: globalThis.PointerEvent) => {
      const dragState = cropDragStateRef.current;

      if (!dragState) {
        return;
      }

      updateCrop(dragState, pointerEvent);
    };
    const handleStopCropDrag = () => {
      cropDragStateRef.current = null;
      removeCropDragListeners();
    };

    removeCropDragListeners();
    cropDragCleanupRef.current = () => {
      window.removeEventListener('pointermove', handleCropPointerMove);
      window.removeEventListener('pointerup', handleStopCropDrag);
    };

    window.addEventListener('pointermove', handleCropPointerMove);
    window.addEventListener('pointerup', handleStopCropDrag);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await onSave(image.publicId, draftEdit);
      onClose();
    } catch {
      // Error is handled by the save callback.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2.1fr) minmax(360px, 0.9fr)' },
            gap: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.900' }}>
            <Box
              ref={cropContainerRef}
              sx={{
                position: 'relative',
                borderRadius: 1.5,
                overflow: 'hidden',
                lineHeight: 0,
              }}
            >
              <Image
                publicId={image.publicId}
                alt={draftEdit.title || image.title || 'Offer image'}
                draggable={false}
                onDragStart={(event) => event.preventDefault()}
                onLoad={(event) => {
                  const { naturalHeight, naturalWidth } = event.currentTarget;

                  if (naturalHeight && naturalWidth) {
                    const nextImageAspectRatio = naturalWidth / naturalHeight;
                    setImageAspectRatio(nextImageAspectRatio);
                    setDraftEdit((currentEdit) =>
                      currentEdit?.cropMode === 'fixed'
                        ? {
                            ...currentEdit,
                            crop: getFixedCrop(currentEdit.crop, nextImageAspectRatio, FIXED_CROP_ASPECT_RATIO),
                          }
                        : currentEdit,
                    );
                  }
                }}
                options={{ width: 1120 }}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'min(680px, calc(100vh - 220px))',
                  objectFit: 'contain',
                  display: 'block',
                  filter: buildImageFilter(draftEdit),
                }}
              />
              {warmthOverlay ? <Box sx={warmthOverlay} /> : null}
              {cropMaskSx.map(({ key, sx }) => (
                <Box key={key} sx={sx} />
              ))}
              <Box sx={getCropFrameSx(draftEdit.crop)} onPointerDown={handleStartCropDrag('move')}>
                {visibleCropHandles.map(({ handle, sx }) => (
                  <Box key={handle} sx={{ ...cropHandleSx, ...sx }} onPointerDown={handleStartCropDrag(handle)} />
                ))}
              </Box>
            </Box>
          </Box>

          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Image editor</Typography>
              <Stack direction="row" spacing={1}>
                <IconButton
                  aria-label="Delete image"
                  color="error"
                  disabled={isSaving}
                  onClick={() => onDeleteImage(image)}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton aria-label="Close image editor" disabled={isSaving} onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>

            <Stack spacing={3} justifyContent="center" sx={{ flexGrow: 1 }}>
              <ImageAdjustmentControls
                adjustmentRange={50}
                adjustments={adjustments}
                cropMode={draftEdit.cropMode}
                fixedAspectRatio={FIXED_CROP_ASPECT_RATIO}
                onAdjustmentsChange={handleAdjustmentsChange}
                onCropModeChange={handleCropModeChange}
                onReset={handleResetAdjustments}
              />

              <Stack spacing={2}>
                <TextField disabled fullWidth label="Filename" value={draftEdit.filename} />
                <TextField
                  fullWidth
                  label="Short description"
                  value={draftEdit.title}
                  onChange={(event) => setDraftEdit({ ...draftEdit, title: event.target.value })}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pt: 0, pb: 2 }}>
        <Button variant="text" disabled={isSaving} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={isSaving} onClick={() => void handleSave()}>
          {isSaving ? 'Saving...' : 'Save image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getCropFrameSx(crop: CropArea) {
  return {
    position: 'absolute',
    top: `${crop.y}%`,
    left: `${crop.x}%`,
    width: `${crop.width}%`,
    height: `${crop.height}%`,
    border: 3,
    borderColor: 'success.main',
    cursor: 'move',
    touchAction: 'none',
  };
}

function getCropMaskSx(crop: CropArea) {
  const right = 100 - crop.x - crop.width;
  const bottom = 100 - crop.y - crop.height;

  return [
    { key: 'top', sx: { top: 0, left: 0, right: 0, height: `${crop.y}%` } },
    { key: 'left', sx: { top: `${crop.y}%`, left: 0, width: `${crop.x}%`, height: `${crop.height}%` } },
    { key: 'right', sx: { top: `${crop.y}%`, right: 0, width: `${right}%`, height: `${crop.height}%` } },
    { key: 'bottom', sx: { left: 0, right: 0, bottom: 0, height: `${bottom}%` } },
  ].map(({ key, sx }) => ({
    key,
    sx: {
      ...sx,
      position: 'absolute',
      bgcolor: 'rgba(0, 0, 0, 0.45)',
      pointerEvents: 'none',
    },
  }));
}

const cropHandleSx = {
  position: 'absolute',
  width: 18,
  height: 18,
  borderRadius: '50%',
  bgcolor: 'success.main',
  border: 3,
  borderColor: 'common.white',
  touchAction: 'none',
};

const cropHandles: Array<{ handle: CropHandle; sx: object }> = [
  { handle: 'top-left', sx: { top: -8, left: -8, cursor: 'nwse-resize' } },
  { handle: 'top', sx: { top: -8, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' } },
  { handle: 'top-right', sx: { top: -8, right: -8, cursor: 'nesw-resize' } },
  { handle: 'left', sx: { top: '50%', left: -8, cursor: 'ew-resize', transform: 'translateY(-50%)' } },
  { handle: 'right', sx: { top: '50%', right: -8, cursor: 'ew-resize', transform: 'translateY(-50%)' } },
  { handle: 'bottom-left', sx: { bottom: -8, left: -8, cursor: 'nesw-resize' } },
  { handle: 'bottom', sx: { bottom: -8, left: '50%', cursor: 'ns-resize', transform: 'translateX(-50%)' } },
  { handle: 'bottom-right', sx: { right: -8, bottom: -8, cursor: 'nwse-resize' } },
];

const fixedCropHandles = cropHandles.filter(({ handle }) => handle.includes('-'));
